import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import UpdateIcon from "@mui/icons-material/Update";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { Link as RouterLink } from "react-router-dom";
import MarketingInfoBar from "./MarketingInfoBar";
import PageMeta from "./PageMeta";

const LAST_UPDATED = "February 11, 2026";

const POLICY_SECTIONS = [
  {
    id: "overview",
    title: "Overview",
    paragraphs: [
      "This Privacy Policy explains how Infrablaze collects, uses, discloses, and protects information when you use our website, platform, and related services.",
      "This policy applies to data we process as a controller for marketing and account administration, and as a processor/service provider for customer cloud infrastructure data, where applicable.",
    ],
  },
  {
    id: "scope",
    title: "Scope and Roles",
    paragraphs: [
      "For website and marketing interactions, Infrablaze typically acts as the data controller.",
      "For customer workload and infrastructure data processed through product integrations, Infrablaze typically acts as a processor on behalf of the customer organization.",
    ],
  },
  {
    id: "data-we-collect",
    title: "Information We Collect",
    paragraphs: ["We may collect the following categories of information:"],
    bullets: [
      "Account and contact data: name, business email, company, role, and communication preferences.",
      "Usage and telemetry data: device/browser metadata, feature usage events, logs, and diagnostics.",
      "Cloud integration metadata: account identifiers, resource metadata, configuration state, dependency context, policy evaluation results, and cost/optimization signals.",
      "Support and communications data: messages, support tickets, and call notes.",
      "Cookie and tracking data: identifiers and analytics information as described in our cookie practices.",
    ],
  },
  {
    id: "how-we-use",
    title: "How We Use Information",
    paragraphs: ["We use information to:"],
    bullets: [
      "Provide, operate, secure, and improve Infrablaze services.",
      "Authenticate users, enforce access controls, and prevent abuse.",
      "Process infrastructure analysis, codification, policy checks, and related automation workflows.",
      "Communicate about product updates, support, billing, and service notices.",
      "Comply with legal obligations, enforce terms, and protect rights and safety.",
    ],
  },
  {
    id: "legal-bases",
    title: "Legal Bases (Where Applicable)",
    paragraphs: [
      "Where required by applicable law (for example, GDPR/UK GDPR), we process personal data based on one or more legal grounds: performance of contract, legitimate interests, consent, and legal obligations.",
    ],
  },
  {
    id: "sharing",
    title: "How We Share Information",
    paragraphs: ["We may share data with:"],
    bullets: [
      "Service providers and subprocessors supporting hosting, analytics, security, communications, and support.",
      "Cloud and integration providers at customer direction.",
      "Professional advisors and auditors under confidentiality obligations.",
      "Government authorities or legal counterparts when required by law or legal process.",
      "A successor entity in connection with a merger, acquisition, or corporate transaction.",
    ],
  },
  {
    id: "international",
    title: "International Data Transfers",
    paragraphs: [
      "If personal data is transferred across borders, we apply appropriate safeguards as required by law, which may include contractual protections and technical measures.",
    ],
  },
  {
    id: "retention",
    title: "Data Retention",
    paragraphs: [
      "We retain personal data for as long as necessary to provide services, satisfy legal and contractual obligations, resolve disputes, and enforce agreements.",
      "Retention periods vary by data type, account status, and legal requirements. Customer-specific retention can also be governed by contract.",
    ],
  },
  {
    id: "security",
    title: "Security",
    paragraphs: [
      "We implement commercially reasonable administrative, technical, and organizational safeguards to protect data. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    id: "cookies",
    title: "Cookies and Similar Technologies",
    paragraphs: [
      "We use cookies and similar technologies for core site functionality, analytics, and service performance. You can manage cookies through browser settings and, where available, cookie consent controls.",
    ],
  },
  {
    id: "ai",
    title: "AI and Model Usage",
    paragraphs: [
      "Infrablaze may use AI-assisted functionality to support infrastructure analysis and recommendations. Customer cloud data is processed according to applicable contracts and access controls.",
      "Unless explicitly agreed in writing, customer-specific infrastructure data is not used to train public foundation models.",
    ],
  },
  {
    id: "rights",
    title: "Your Privacy Rights",
    paragraphs: [
      "Depending on your jurisdiction, you may have rights to access, correct, delete, restrict, or object to certain processing, and to request data portability.",
      "You may also opt out of non-essential marketing communications at any time.",
      "To exercise rights, contact us at contact@infrablaze.com.",
    ],
  },
  {
    id: "children",
    title: "Children's Privacy",
    paragraphs: [
      "Infrablaze services are intended for business use and are not directed to children under 16. We do not knowingly collect personal data from children.",
    ],
  },
  {
    id: "third-party-links",
    title: "Third-Party Links and Services",
    paragraphs: [
      "Our site and services may link to third-party websites or services. Their privacy practices are governed by their own policies, not this Privacy Policy.",
    ],
  },
  {
    id: "changes",
    title: "Changes to this Policy",
    paragraphs: [
      "We may update this Privacy Policy periodically. Material updates will be reflected by revising the 'Last updated' date and, where appropriate, by additional notice.",
    ],
  },
  {
    id: "contact",
    title: "Contact Us",
    paragraphs: [
      "For privacy questions or requests, contact contact@infrablaze.com.",
    ],
  },
];

export default function PrivacyPolicyPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [activeId, setActiveId] = useState(POLICY_SECTIONS[0].id);
  const [copied, setCopied] = useState("");
  const sectionRefs = useRef({});

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );

    POLICY_SECTIONS.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <Box component="main" sx={{ flex: 1, display: "flex", flexDirection: "column", overflowX: "hidden" }}>
      <PageMeta
        title="Privacy Policy · InfraBlaze"
        description="How InfraBlaze, Inc. collects, uses, discloses, and protects personal and customer cloud infrastructure data across our website, platform, and related services."
        canonicalPath="/privacy-policy"
      />
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          pt: {
            xs: "calc(56px + env(safe-area-inset-top) + 56px)",
            md: "calc(56px + env(safe-area-inset-top) + 88px)",
          },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            width: { xs: 500, md: 900 },
            height: { xs: 500, md: 900 },
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.18 : 0.09)}, transparent 60%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Container
          maxWidth="md"
          sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}
        >
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <Typography
              variant="h1"
              sx={{
                fontWeight: 950,
                letterSpacing: -1.0,
                lineHeight: 1.04,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.05rem" },
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ maxWidth: 640, lineHeight: 1.7, fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.4rem" } }}
            >
              How Infrablaze handles personal data across our marketing website
              and cloud automation platform.
            </Typography>

            {/* Meta row */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems="center"
              sx={{ pt: 1 }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 999,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22),
                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08),
                }}
              >
                <UpdateIcon sx={{ fontSize: 16, color: "primary.main" }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "primary.main", letterSpacing: 0.3 }}
                >
                  Last updated · {LAST_UPDATED}
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/terms-of-use"
                size="small"
                startIcon={<ArticleOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Terms of Use
              </Button>
              <Button
                component="a"
                href="mailto:contact@infrablaze.com"
                size="small"
                startIcon={<MailOutlineIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "text.secondary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Contact legal
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Box sx={{ pb: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
            {/* Scroll-spy Table of Contents */}
            <Grid item xs={12} md={3.5}>
              <Box
                sx={{
                  position: { md: "sticky" },
                  top: { md: 100 },
                  maxHeight: { md: "calc(100vh - 120px)" },
                  overflowY: { md: "auto" },
                  pr: { md: 1 },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    color: "text.secondary",
                    mb: 1.5,
                    fontSize: "0.72rem",
                  }}
                >
                  ON THIS PAGE
                </Typography>
                <Stack
                  spacing={0}
                  sx={{
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 4,
                      bottom: 4,
                      width: "2px",
                      background: alpha(theme.palette.divider, 0.8),
                      borderRadius: 2,
                    },
                  }}
                >
                  {POLICY_SECTIONS.map((section) => {
                    const active = activeId === section.id;
                    return (
                      <Box
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          pl: 2,
                          py: 0.7,
                          transition: "all 0.25s ease",
                          borderLeft: "2px solid",
                          borderColor: active
                            ? theme.palette.primary.main
                            : "transparent",
                          ml: "-1px",
                          "&:hover": {
                            "& .toc-label": { color: theme.palette.primary.main },
                          },
                        }}
                      >
                        <Typography
                          className="toc-label"
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: active ? 800 : 600,
                            color: active ? "primary.main" : "text.secondary",
                            lineHeight: 1.45,
                            transition: "color 0.25s ease, font-weight 0.25s ease",
                          }}
                        >
                          {section.title}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Grid>

            {/* Document body */}
            <Grid item xs={12} md={8.5}>
              <Box sx={{ maxWidth: 760 }}>
                {POLICY_SECTIONS.map((section, idx) => (
                  <Box
                    key={section.id}
                    id={section.id}
                    ref={(el) => (sectionRefs.current[section.id] = el)}
                    sx={{
                      scrollMarginTop: "90px",
                      pb: { xs: 4, md: 5 },
                      mb: { xs: 4, md: 5 },
                      borderBottom:
                        idx < POLICY_SECTIONS.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                          : "none",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        mb: 2,
                        "&:hover .copy-link": { opacity: 1 },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 900,
                          letterSpacing: -0.4,
                          lineHeight: 1.2,
                          fontSize: { xs: "1.45rem", md: "1.7rem" },
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Tooltip title={copied === section.id ? "Copied!" : "Copy link"}>
                        <IconButton
                          className="copy-link"
                          size="small"
                          onClick={() => copyLink(section.id)}
                          sx={{
                            opacity: 0,
                            transition: "opacity 0.25s ease",
                            color: "text.secondary",
                            "&:hover": { color: "primary.main" },
                          }}
                        >
                          <LinkIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Stack spacing={2}>
                      {(section.paragraphs || []).map((text, i) => (
                        <Typography
                          key={i}
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.85,
                            fontSize: "1rem",
                          }}
                        >
                          {text}
                        </Typography>
                      ))}
                      {(section.bullets || []).length > 0 ? (
                        <Box
                          component="ul"
                          sx={{
                            m: 0,
                            pl: 0,
                            listStyle: "none",
                            "& > li": {
                              position: "relative",
                              pl: 3,
                              py: 0.5,
                              color: "text.secondary",
                              lineHeight: 1.8,
                              fontSize: "0.98rem",
                              "&::before": {
                                content: '""',
                                position: "absolute",
                                left: 4,
                                top: 14,
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.primary.main,
                                boxShadow: `0 0 10px ${alpha(theme.palette.primary.main, 0.4)}`,
                              },
                            },
                          }}
                        >
                          {section.bullets.map((item) => (
                            <Typography key={item} component="li">
                              {item}
                            </Typography>
                          ))}
                        </Box>
                      ) : null}
                    </Stack>
                  </Box>
                ))}

                {/* Bottom contact card */}
                <Box
                  sx={{
                    mt: 2,
                    p: { xs: 3, md: 4 },
                    borderRadius: "16px",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.25),
                    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.5 : 0.92),
                    backdropFilter: "blur(10px)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background: `radial-gradient(400px 150px at 0% 0%, ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12)}, transparent 60%)`,
                      pointerEvents: "none",
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                      Questions about your data?
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ mb: 2.5, lineHeight: 1.75 }}
                    >
                      Reach out to our team for data requests, DPA execution, or any
                      privacy-related questions.
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <Button
                        component="a"
                        href="mailto:contact@infrablaze.com"
                        variant="contained"
                        startIcon={<MailOutlineIcon />}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 999,
                          px: 3,
                          textTransform: "none",
                          color: "common.white !important",
                          "&:hover": { color: "common.white !important" },
                        }}
                      >
                        contact@infrablaze.com
                      </Button>
                      <Button
                        component={RouterLink}
                        to="/terms-of-use"
                        variant="outlined"
                        sx={{
                          fontWeight: 900,
                          borderRadius: 999,
                          px: 3,
                          textTransform: "none",
                          borderWidth: 1.5,
                          "&:hover": { borderWidth: 1.5 },
                        }}
                      >
                        View Terms of Use
                      </Button>
                    </Stack>
                  </Box>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 3, lineHeight: 1.7, fontStyle: "italic", opacity: 0.8 }}
                >
                  This page provides general policy terms. Where a signed enterprise
                  agreement contains privacy or data processing terms, those terms govern
                  in case of conflict.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <MarketingInfoBar />
      </Box>
    </Box>
  );
}
